// FORCE REBUILD: 2026-02-07-shopify-calendly
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, DollarSign, Shield, CheckCircle, Loader2 } from 'lucide-react';

// Shopify configuration from environment variables
const SHOPIFY_CONFIG = {
  domain: process.env.REACT_APP_SHOPIFY_DOMAIN,
  storefrontAccessToken: process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN,
};

// Product IDs from Shopify (full GID format required by SDK)
const SHOPIFY_PRODUCTS = {
  silver: process.env.REACT_APP_SHOPIFY_PRODUCT_SILVER,
  gold: process.env.REACT_APP_SHOPIFY_PRODUCT_GOLD,
  platinum: process.env.REACT_APP_SHOPIFY_PRODUCT_PLATINUM,
  individual: process.env.REACT_APP_SHOPIFY_PRODUCT_INDIVIDUAL,
  couples: process.env.REACT_APP_SHOPIFY_PRODUCT_COUPLES,
};

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [shopifyClient, setShopifyClient] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  const services = [
    { id: 'silver', name: 'Silver Package - Monthly', price: 500, description: 'Stabilization & Accountability' },
    { id: 'gold', name: 'Gold Package - Monthly', price: 850, description: 'Integrated Recovery Coaching' },
    { id: 'platinum', name: 'Platinum Package - Monthly', price: 1500, description: 'High-Touch Sober Coaching' },
    { id: 'individual', name: 'Individual Session', price: 150, description: 'One-on-one personalized coaching (1 hour)' },
    { id: 'couples', name: 'Couples/Family Session', price: 200, description: 'Group coaching session (1 hour)' },
    { id: 'custom', name: 'Custom Amount', price: 0, description: 'Enter your own amount' }
  ];

  // Load Shopify Buy Button SDK
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const loadShopifySDK = () => {
      if (window.ShopifyBuy) {
        initShopify();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
      script.onload = initShopify;
      document.head.appendChild(script);
    };

    const initShopify = () => {
      if (window.ShopifyBuy) {
        const client = window.ShopifyBuy.buildClient({
          domain: SHOPIFY_CONFIG.domain,
          storefrontAccessToken: SHOPIFY_CONFIG.storefrontAccessToken,
        });
        setShopifyClient(client);
        setSdkLoaded(true);
      }
    };

    loadShopifySDK();
  }, []);

  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      setSelectedService(serviceParam);
    }
  }, [searchParams]);

  const getSelectedPrice = () => {
    if (selectedService === 'custom') {
      return parseFloat(customAmount) || 0;
    }
    const service = services.find(s => s.id === selectedService);
    return service ? service.price : 0;
  };

  const getSelectedServiceName = () => {
    const service = services.find(s => s.id === selectedService);
    return service ? service.name : 'Select a service';
  };

  const handlePayment = useCallback(async () => {
    const amount = getSelectedPrice();
    if (amount <= 0) {
      alert('Please select a service or enter a valid amount');
      return;
    }

    if (selectedService === 'custom') {
      // For custom amounts, redirect to contact page
      alert('For custom payment amounts, please contact us directly to arrange payment.');
      window.location.href = '/contact';
      return;
    }

    if (!sdkLoaded || !shopifyClient) {
      alert('Payment system is loading. Please wait a moment and try again.');
      return;
    }

    setIsProcessing(true);

    try {
      const productId = SHOPIFY_PRODUCTS[selectedService];
      
      if (!productId) {
        alert('Service not found. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Fetch the product to get variant ID
      const product = await shopifyClient.product.fetch(productId);
      
      if (!product || !product.variants || product.variants.length === 0) {
        alert('Product not found. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Get the first variant
      const variantId = product.variants[0].id;

      // Create checkout with the variant
      const checkout = await shopifyClient.checkout.create();
      const lineItemsToAdd = [{ variantId: variantId, quantity: 1 }];
      
      await shopifyClient.checkout.addLineItems(checkout.id, lineItemsToAdd);
      
      // Redirect to Shopify checkout
      window.location.href = checkout.webUrl;

    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
      setIsProcessing(false);
    }
  }, [selectedService, sdkLoaded, shopifyClient, customAmount]);

  return (
    <main className="payment-page" data-testid="payment-page">
      <div className="payment-header">
        <h1>Make a <span>Payment</span></h1>
        <p>
          Securely pay for your coaching services. Select a package or enter a custom amount.
        </p>
      </div>

      <div className="payment-content">
        <div className="payment-card">
          <h2><CreditCard size={24} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />Select Service</h2>
          
          {/* Service Dropdown */}
          <div className="service-selector">
            <label htmlFor="service">Choose a Service</label>
            <select
              id="service"
              className="service-select"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              data-testid="service-select"
            >
              <option value="">-- Select a Service --</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} {service.price > 0 ? `- $${service.price.toLocaleString()}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Amount Section */}
          {selectedService === 'custom' && (
            <div className="custom-amount-section" data-testid="custom-amount-section">
              <h3><DollarSign size={20} /> Enter Custom Amount</h3>
              <div className="custom-input-group">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  className="custom-input"
                  placeholder="0.00"
                  min="1"
                  step="0.01"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  data-testid="custom-amount-input"
                />
              </div>
              <p className="custom-note">For custom amounts, you'll be redirected to contact us.</p>
            </div>
          )}

          {/* Selected Amount Display */}
          {selectedService && (
            <div className="selected-amount" data-testid="selected-amount">
              <p>{getSelectedServiceName()}</p>
              <div className="amount">${getSelectedPrice().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          )}

          {/* Pay Button */}
          <button
            className="pay-btn"
            onClick={handlePayment}
            disabled={!selectedService || getSelectedPrice() <= 0 || isProcessing}
            data-testid="pay-btn"
          >
            {isProcessing ? (
              <>
                <Loader2 size={20} className="spin" />
                Processing...
              </>
            ) : (
              <>
                <Shield size={20} />
                Pay Securely with Shopify
              </>
            )}
          </button>

          {!sdkLoaded && (
            <p className="loading-note">
              <Loader2 size={14} className="spin" style={{ display: 'inline', marginRight: '5px' }} />
              Loading payment system...
            </p>
          )}

          <p className="payment-note">
            <CheckCircle size={14} style={{ display: 'inline', marginRight: '5px' }} />
            Secure payment powered by Shopify. Your information is protected.
          </p>

          {/* Payment Method Logos */}
          <div className="payment-logos" data-testid="payment-logos">
            <p>Accepted Payment Methods</p>
            <div className="logos-grid">
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-visa"><title id="pi-visa">Visa</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z" fill="#142688"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-mastercard"><title id="pi-mastercard">Mastercard</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><circle fill="#EB001B" cx="15" cy="12" r="7"/><circle fill="#F79E1B" cx="23" cy="12" r="7"/><path fill="#FF5F00" d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-amex"><title id="pi-amex">American Express</title><path fill="#000" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" opacity=".07"/><path fill="#006FCF" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#FFF" d="M22.012 19.936v-8.421L37 11.528v2.326l-1.732 1.852L37 17.573v2.375h-2.766l-1.47-1.622-1.46 1.628-9.292-.02z"/><path fill="#006FCF" d="M23.013 19.012v-6.57h5.572v1.513h-3.768v1.028h3.678v1.488h-3.678v1.01h3.768v1.531h-5.572z"/><path fill="#006FCF" d="M28.557 19.012L31.303 15.6l-2.746-3.413h2.26l1.602 2.015 1.623-2.015h2.191l-2.69 3.348 2.746 3.477h-2.247l-1.68-2.095-1.686 2.095h-2.12z"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-discover"><title id="pi-discover">Discover</title><path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" fill="#000" opacity=".07"/><path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/><path d="M37 16.95V21c0 1.1-.9 2-2 2H23.228c7.896-1.815 12.043-4.601 13.772-6.05z" fill="#EDA024"/><path fill="#494949" d="M9 11h20v2H9z"/><circle cx="19" cy="12" r="4" fill="#EDA024"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-apple-pay"><title id="pi-apple-pay">Apple Pay</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#000" d="M12.3 8.4c.3-.4.5-.9.5-1.4-.5 0-1.1.3-1.5.7-.3.3-.6.9-.5 1.4.5 0 1.1-.3 1.5-.7zm.5.8c-.8 0-1.6.5-2 .5s-1-.5-1.7-.5c-.9 0-1.7.5-2.1 1.3-.9 1.6-.2 3.9.6 5.2.4.6.9 1.3 1.6 1.3.6 0 .9-.4 1.6-.4.7 0 .9.4 1.6.4.7 0 1.1-.6 1.5-1.2.5-.7.7-1.4.7-1.4s-1.3-.5-1.3-2 1.1-2.2 1.1-2.2c-.6-.9-1.5-1-1.6-1z"/><path fill="#000" d="M24.6 7.7c1.9 0 3.3 1.3 3.3 3.3 0 2-1.4 3.3-3.4 3.3h-2.2v3.4H20v-10h4.6zm-2.3 5h1.8c1.3 0 2.1-.7 2.1-1.8 0-1.1-.8-1.8-2.1-1.8h-1.8v3.6z"/><path fill="#000" d="M28.6 13.3c0-1.3 1-2.1 2.8-2.2l2 0v-.6c0-.8-.5-1.3-1.5-1.3-.8 0-1.4.4-1.5 1h-2c.1-1.5 1.4-2.6 3.6-2.6 2.1 0 3.4 1.1 3.4 2.8v5.9h-2v-1.4c-.5 1-1.4 1.6-2.6 1.6-1.6 0-2.7-1-2.7-2.5l.5.3zm4.9-.7v-.7l-1.8.1c-.9.1-1.4.4-1.4 1.1s.6 1 1.3 1c1.1 0 1.9-.7 1.9-1.5z"/><path fill="#000" d="M36.3 17.8h-1.6c.1-.3.3-1.1.8-2.7l2-6.7h2.3l-3 9.4h-.5z"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-google-pay"><title id="pi-google-pay">Google Pay</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#4285F4" d="M28.8 11.3c0-.4 0-.8-.1-1.1h-5.2v2.1h3c-.1.8-.5 1.4-1.1 1.8v1.5h1.8c1-1 1.6-2.4 1.6-4.3z"/><path fill="#34A853" d="M23.5 16.5c1.5 0 2.7-.5 3.6-1.3l-1.8-1.4c-.5.3-1.1.5-1.8.5-1.4 0-2.5-.9-2.9-2.2h-1.8v1.4c.9 1.8 2.7 3 4.7 3z"/><path fill="#FBBC05" d="M20.6 12c-.2-.5-.2-1.1 0-1.6V9h-1.8c-.7 1.2-.7 2.7 0 3.9l1.8-1z"/><path fill="#EA4335" d="M23.5 8.6c.8 0 1.5.3 2 .8l1.5-1.5c-1-.9-2.2-1.4-3.5-1.4-2 0-3.8 1.2-4.7 3l1.8 1.4c.4-1.3 1.5-2.3 2.9-2.3z"/><path fill="#4285F4" d="M15.2 10.8h-.4v2.4h.4c.7 0 1.2-.5 1.2-1.2s-.5-1.2-1.2-1.2z"/><path fill="#4285F4" d="M18.4 10.8h-1.4v4.4h-1v-4.4h-1.4v-.9h3.8v.9z"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-paypal"><title id="pi-paypal">PayPal</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#003087" d="M23.9 8.3c.2-1 0-1.7-.6-2.3-.6-.7-1.7-1-3.1-1h-4.1c-.3 0-.5.2-.6.5L14 15.6c0 .2.1.4.3.4H17l.4-3.4 1.8-2.2 4.7-2.1z"/><path fill="#3086C8" d="M23.9 8.3l-.2.2c-.5 2.8-2.2 3.8-4.6 3.8H18c-.3 0-.5.2-.6.5l-.6 3.9-.2 1c0 .2.1.4.3.4H19c.3 0 .5-.2.5-.4v-.1l.4-2.4v-.1c0-.2.3-.4.5-.4h.3c2.1 0 3.7-.8 4.2-3.3.2-1 .1-1.8-.4-2.4-.1-.2-.3-.4-.6-.7z"/><path fill="#012169" d="M23.3 8.1c-.1-.1-.2-.1-.3-.1-.1 0-.2 0-.3-.1-.3-.1-.7-.1-1.1-.1h-3c-.1 0-.2 0-.2.1-.2.1-.3.2-.3.4l-.7 4.4v.1c0-.3.3-.5.6-.5h1.3c2.5 0 4.1-1 4.6-3.8v-.2c-.1-.1-.3-.2-.6-.2z"/></svg>
              <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-labelledby="pi-shoppay"><title id="pi-shoppay">Shop Pay</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#5A31F4" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#fff" d="M13.3 9.4c-.5-.2-1-.4-1.5-.4-.8 0-1.1.4-1.1.7 0 .5.5.6 1.1.8.9.3 2 .6 2 1.9 0 1.5-1.2 2.2-2.8 2.2-.9 0-1.7-.2-2.4-.6l.5-1.2c.5.3 1.2.5 1.8.5.7 0 1.2-.3 1.2-.8 0-.5-.5-.7-1.2-.9-.9-.3-1.9-.6-1.9-1.9 0-1.3 1.1-2.1 2.6-2.1.8 0 1.5.2 2.1.4l-.4 1.4zm5.6-1.6h2.8c1.7 0 2.9.9 2.9 2.5s-1.2 2.6-2.9 2.6h-1.4v2.4h-1.4V7.8zm2.6 3.8c1 0 1.6-.5 1.6-1.3s-.6-1.2-1.6-1.2h-1.2v2.5h1.2zm6.1 3.7l-.1-.6c-.4.5-1 .8-1.7.8-1.1 0-1.9-.7-1.9-1.7 0-1.2 1-1.8 2.4-1.8h1.2v-.3c0-.6-.4-1-1.2-1-.6 0-1.1.2-1.6.6l-.6-.9c.7-.5 1.5-.8 2.5-.8 1.5 0 2.3.8 2.3 2.2v3.5h-1.3zm-.1-2.4h-1c-.8 0-1.2.3-1.2.8s.4.8.9.8c.7 0 1.3-.5 1.3-1.2v-.4zm3.9 3.1l-2-5.4h1.5l1.3 4 1.4-4h1.4l-2.6 6.6c-.4 1-1 1.5-2 1.5-.4 0-.7-.1-1-.2l.3-1.1c.2.1.4.1.6.1.4 0 .7-.2.9-.6l.2-.9z"/></svg>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PaymentPage;
